import type { FC } from 'react';

/**
 * Props passed to a tool's progress component.
 *
 * Progress components are tiny inline renderers — they receive the tool's
 * `args` and (when available) `result`, and return a short ReactNode slot
 * that follows the "AiPSO Agent is ..." prefix. Deliberately narrower than
 * `ToolCallMessagePartProps`: progress components never need `addResult` /
 * `resume` / aui part context — they render outside the per-part primitive.
 */
export interface ToolProgressProps<ArgsType, ResultType> {
  args?: ArgsType;
  result?: ResultType;
  toolCallId?: string;
}

/**
 * A tool progress component. Returns inline content that follows the
 * "AiPSO Agent is" prefix in the progress bubble (e.g. "looking up data...").
 */
export type ToolProgressComponent<ArgsType = unknown, ResultType = unknown> = FC<
  ToolProgressProps<ArgsType, ResultType>
>;

/**
 * Progress component registry — module-level map of tool name → progress component.
 *
 * A tool becomes "collapsible" by declaring a `progress` component on its
 * `ToolConfig`. `createTool` registers/unregisters the component here alongside
 * the `setToolUI` lifecycle. The chat thread reads this registry to decide
 * which consecutive tool parts form a collapsible run.
 *
 * Collapsibility is a UI-only concern: the tool name is the only key the
 * registry needs, and registration is idempotent per tool. A plain `Map` is
 * sufficient because progress components are registered on mount before any
 * assistant message that references them can render.
 */
// biome-ignore lint/suspicious/noExplicitAny: Registry erases per-tool arg/result types; consumers supply the types at the call site.
const registry = new Map<string, ToolProgressComponent<any, any>>();

/**
 * Register a progress component for a tool. Returns an unregister function.
 * Intended to be called from `createTool`'s effect.
 */
export function registerProgressComponent(
  toolName: string,
  // biome-ignore lint/suspicious/noExplicitAny: See registry declaration above.
  component: ToolProgressComponent<any, any>,
): () => void {
  registry.set(toolName, component);
  return () => {
    if (registry.get(toolName) === component) {
      registry.delete(toolName);
    }
  };
}

/**
 * Get the progress component for a tool, or `undefined` if the tool did not
 * declare one (i.e. is not collapsible).
 */
// biome-ignore lint/suspicious/noExplicitAny: See registry declaration above.
export function getProgressComponent(toolName?: string): ToolProgressComponent<any, any> | undefined {
  if (!toolName) {
    return undefined;
  }
  return registry.get(toolName);
}

/**
 * Predicate: does this tool declare a progress component (and therefore
 * participate in collapsible runs)?
 */
export function isCollapsibleTool(toolName: string): boolean {
  return registry.has(toolName);
}
